import type { ReferencedResourceId } from "./common";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";

export type RelatedPersonId = DomainId<"RelatedPersonId">;
export function asRelatedPersonId(value: string): RelatedPersonId {
  if (!value.trim()) throw new PreconditionFailedError("A related person identity cannot be empty");
  return value as RelatedPersonId;
}

export const RELATED_PERSON_RELATIONSHIPS = [
  "parent",
  "guardian",
  "spouse",
  "sibling",
  "child",
  "caregiver",
  "partner",
  "other",
] as const;
export type RelatedPersonRelationship = (typeof RELATED_PERSON_RELATIONSHIPS)[number];
export interface RelatedPersonName {
  given: string[];
  family?: string;
}
export interface RelatedPersonDetails {
  name: RelatedPersonName;
  relationships: RelatedPersonRelationship[];
  email: string;
  phone?: string;
}
export interface RelatedPersonPortalGrant {
  membershipId: ReferencedResourceId;
  userId: ReferencedResourceId;
  email: string;
}
export interface RelatedPersonProps extends RelatedPersonDetails {
  portalGrant?: RelatedPersonPortalGrant;
  id: RelatedPersonId;
  patientId: PatientId;
  active: boolean;
  isEmergencyContact: boolean;
}

function details(input: RelatedPersonDetails): RelatedPersonDetails {
  const name = {
    given: input.name.given.map((value) => value.trim()).filter(Boolean),
    family: input.name.family?.trim() || undefined,
  };
  if (!name.given.length && !name.family) throw new PreconditionFailedError("A related person requires a name");
  if (
    !input.relationships.length ||
    input.relationships.some((value) => !RELATED_PERSON_RELATIONSHIPS.includes(value))
  ) {
    throw new PreconditionFailedError("A related person requires a known relationship");
  }
  const email = input.email?.trim() || undefined;
  const phone = input.phone?.trim() || undefined;
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    throw new PreconditionFailedError("A related person email must be valid");
  return {
    name,
    relationships: RELATED_PERSON_RELATIONSHIPS.filter((value) => input.relationships.includes(value)),
    email,
    phone,
  };
}

export type StoredRelatedPersonProps = Omit<RelatedPersonProps, "active"> & { active?: boolean };

/** One person's relationship to one patient. Neither active nor a role grants portal access. */
export class RelatedPerson {
  private state: RelatedPersonProps;
  private constructor(props: RelatedPersonProps) {
    if (!props.patientId?.trim()) throw new PreconditionFailedError("A related person requires a patient");
    this.state = { ...structuredClone(props), id: asRelatedPersonId(props.id), ...details(props) };
  }
  static create(
    input: RelatedPersonDetails & { id: string; patientId: PatientId; isEmergencyContact?: boolean },
  ): RelatedPerson {
    return new RelatedPerson({
      ...input,
      id: asRelatedPersonId(input.id),
      active: true,
      isEmergencyContact: input.isEmergencyContact ?? false,
    });
  }
  static restore(snapshot: StoredRelatedPersonProps): RelatedPerson {
    return new RelatedPerson({ ...snapshot, active: snapshot.active ?? false });
  }
  get id(): RelatedPersonId {
    return this.state.id;
  }
  get patientId(): PatientId {
    return this.state.patientId;
  }
  get isActive(): boolean {
    return this.state.active;
  }
  get hasPortalAccess(): boolean {
    const grant = this.state.portalGrant;
    return this.state.active && !!grant && grant.email.toLowerCase() === this.state.email.toLowerCase();
  }
  snapshot(): RelatedPersonProps {
    return structuredClone(this.state);
  }
  /** Full replacement of editable details. Identity, patient and roles cannot change through this operation. */
  updateDetails(input: RelatedPersonDetails): void {
    const next = details(input);
    if (this.state.portalGrant && next.email.toLowerCase() !== this.state.email.toLowerCase()) {
      throw new PreconditionFailedError("Revoke portal access before changing the approved email");
    }
    this.state = { ...this.state, ...next };
  }
  deactivate(): void {
    if (this.state.portalGrant)
      throw new PreconditionFailedError("Revoke portal access before deactivating this related person");
    this.state.active = false;
  }
  reactivate(): void {
    this.state.active = true;
  }
  approvePortalAccess(grant: RelatedPersonPortalGrant): void {
    if (!this.state.active) throw new PreconditionFailedError("Only active related persons can receive portal access");
    if (!grant.membershipId || !grant.userId || grant.email.toLowerCase() !== this.state.email.toLowerCase()) {
      throw new PreconditionFailedError("Portal access must bind the approved email to an account");
    }
    this.state.portalGrant = structuredClone(grant);
  }
  revokePortalAccess(): void {
    this.state.portalGrant = undefined;
  }
  designateEmergencyContact(): void {
    this.state.isEmergencyContact = true;
  }
  removeEmergencyContactDesignation(): void {
    this.state.isEmergencyContact = false;
  }
}
